import React, { useEffect, useRef, useState } from 'react';
import { arranquePara, cuentaDe, pctDe, ESCENARIOS, HORA_RANGO, HORAS_SEMANA, PCT_PROPIO } from '../data/libera';
import { evento } from '../lib/medicion';

// La calculadora como un jueguito (An, 04/11: «que sea un jueguito»). La persona pone solo lo que sabe: cuántas
// personas son y cuántas horas por semana trabaja cada una. Cuánto se libera no se pregunta: es un filtro del
// resultado (pesimista 10 %, conservador 20 % o el propio). El resultado muestra la cuenta renglón por renglón,
// como en la propuesta de REMAX (An, 04/11: «tiene que mostrar la cuenta; está críptica»), y la plata va aparte,
// con el valor hora que la persona puede cambiar. Sin «un empleado más» (An, 04/11).
const pesos = (n) => `$${Math.round(n).toLocaleString('es-AR')}`;
const num = (x) => x.toLocaleString('es-AR', { maximumFractionDigits: 1 });

// La cifra cuenta hasta el valor nuevo en vez de saltar; con movimiento reducido, salta.
const useContado = (valor) => {
  const [mostrado, setMostrado] = useState(valor);
  const desde = useRef(valor);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setMostrado(valor); desde.current = valor; return undefined; }
    const inicio = performance.now(); const de = desde.current;
    let id;
    const paso = (t) => {
      const k = Math.min(1, (t - inicio) / 500);
      const v = de + (valor - de) * (1 - (1 - k) ** 3);
      setMostrado(v); desde.current = v;
      if (k < 1) id = requestAnimationFrame(paso);
    };
    id = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(id);
  }, [valor]);
  return mostrado;
};

const CuantoLibera = ({ collected, producto }) => {
  const [n, setN] = useState(() => arranquePara(collected));
  const [horaTexto, setHoraTexto] = useState('');
  const medida = useRef(null);
  // La cuenta y el valor hora quedan ocultos: se abren tocando la cifra o el monto (An, 04/11: «el cálculo puede
  // estar oculto y al hacer click en el número se ve»).
  const [verCuenta, setVerCuenta] = useState(false);
  const [verHora, setVerHora] = useState(false);
  const pon = (cambio) => setN(p => ({ ...p, ...cambio }));
  const c = cuentaDe(n);
  const pct = pctDe(n);
  const uno = n.personas === 1;
  const horasSemana = Math.round(c.horasSemana * 2) / 2;
  const contado = useContado(horasSemana);
  const personas = (k) => pon({ personas: Math.max(1, Math.min(50, n.personas + k)) });

  // El valor hora se confirma con Enter (o al salir del campo): recién ahí cambia la cuenta y se mide, con el
  // tamaño del equipo, las horas y el escenario de ese momento. Un mismo valor no se mide dos veces.
  const confirmarHora = () => {
    const v = Number(horaTexto.replace(/\D/g, ''));
    if (!v || v < HORA_RANGO.min || v > HORA_RANGO.max) { setHoraTexto(n.hora ? String(n.hora) : ''); return; }
    pon({ hora: v });
    setHoraTexto(String(v));
    const clave = `${v}-${n.personas}-${n.horas}-${pct}`;
    if (medida.current !== clave) {
      medida.current = clave;
      evento('valor_hora', { valor: v, opcion: `${n.personas}p-${n.horas}h-${pct}` });
    }
  };

  return (
    <div className="gh-libera gh-juego">
      {/* Lo que pone la persona, dicho como una frase (04/11, «está llena de palabras»): sin pasos numerados */}
      <div className="gh-libera__mandos gh-frase">
        <span>Somos</span>
        <span className="gh-frase__num" role="group" aria-label="Cantidad de personas">
          <button type="button" className="gh-juego__mas" onClick={() => personas(-1)} disabled={n.personas <= 1} aria-label="Una persona menos">−</button>
          <input type="text" inputMode="numeric" value={n.personas} aria-label="Cantidad de personas"
            onChange={e => { const v = Number(e.target.value.replace(/\D/g, '')); if (v) pon({ personas: Math.min(50, v) }); }} />
          <button type="button" className="gh-juego__mas" onClick={() => personas(1)} disabled={n.personas >= 50} aria-label="Una persona más">+</button>
        </span>
        <span>{uno ? 'persona.' : 'personas.'}</span>
        <span className="gh-frase__horas-grupo">
          <span className="gh-frase__horas" role="radiogroup" aria-label="Horas por semana de cada una">
            {HORAS_SEMANA.map(o => (
              <button key={o.h} type="button" role="radio" aria-checked={n.horas === o.h} onClick={() => pon({ horas: o.h })}>
                {o.texto}<small>{o.nota}</small>
              </button>
            ))}
          </span>
          <small className="gh-frase__pie">{uno ? 'Horas que trabajás por semana' : 'Horas por semana de cada una'}</small>
        </span>
      </div>

      <div className="gh-libera__resultado">
        {/* El escenario: un solo filtro, tres opciones iguales; el porcentaje propio se ajusta adentro de su opción */}
        <div className="gh-filtro" role="radiogroup" aria-label="Escenario">
          {ESCENARIOS.map(e => {
            const activo = n.escenario === e.id;
            const valor = e.pct ?? n.pctPropio;
            return (
              <div key={e.id} className={`gh-filtro__opcion${activo ? ' is-activo' : ''}`}>
                <button type="button" role="radio" aria-checked={activo} onClick={() => pon({ escenario: e.id })}>
                  <span className="gh-filtro__nombre">{e.texto}</span>
                  <span className="gh-filtro__pct">{valor} %</span>
                </button>
                {e.id === 'propio' && activo && (
                  <span className="gh-filtro__ajuste">
                    <button type="button" onClick={() => pon({ pctPropio: Math.max(PCT_PROPIO.min, n.pctPropio - 5) })} aria-label="5 % menos">−</button>
                    <button type="button" onClick={() => pon({ pctPropio: Math.min(PCT_PROPIO.max, n.pctPropio + 5) })} aria-label="5 % más">+</button>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* La cifra y su barra son una sola cosa; el triángulo abre la cuenta debajo */}
        <p className="gh-libera__con">{producto ? <>Con <strong>{producto}</strong>, </> : ''}{uno ? 'recuperás' : 'entre todos recuperan'}</p>
        <button type="button" className="gh-resultado" aria-expanded={verCuenta} aria-controls="gh-j-cuenta" aria-label={`${num(horasSemana)} horas por semana. ${verCuenta ? 'Cerrar' : 'Ver'} la cuenta`} onClick={() => setVerCuenta(v => !v)}>
          <span className="gh-libera__cifra">
            <b>{num(Math.round(contado * 2) / 2)}</b>
            <span className="gh-resultado__unidad">horas por semana<i className="gh-abre" aria-hidden="true" /></span>
          </span>
          {/* La parte liberada sobre todo lo que trabaja el equipo, en la misma medida (horas) */}
          <span className="gh-resultado__barra" aria-hidden="true"><i style={{ transform: `scaleX(${Math.min(1, c.horasSemana / c.totalSemana)})` }} /></span>
          <span className="gh-resultado__de">de {num(c.totalSemana)} h que {uno ? 'trabajás' : 'trabaja el equipo'} por semana</span>
        </button>
        {verCuenta && (
          <dl className="gh-juego__cuenta-lista" id="gh-j-cuenta">
            <div><dt>Cada persona trabaja</dt><dd>{n.horas} h por semana</dd></div>
            <div><dt>La IA le ahorra el {pct} %</dt><dd>{num(c.porPersona)} h por semana</dd></div>
            {!uno && <div><dt>{n.personas} personas × {num(c.porPersona)} h</dt><dd>{num(c.horasSemana)} h por semana</dd></div>}
            <div><dt>Por mes, 4 semanas</dt><dd>{num(Math.round(c.horasMes))} h</dd></div>
          </dl>
        )}

        {/* La plata, aparte, con el mismo triángulo: abre el valor hora (An, 04/11: «la hora es algo aparte») */}
        <button type="button" className="gh-plata" aria-expanded={verHora} aria-controls="gh-j-plata" onClick={() => setVerHora(v => !v)}>
          <span>{c.pesosMes ? <>Ese tiempo vale <b>{pesos(c.pesosMes)}</b> por mes</> : '¿Cuánto vale ese tiempo?'}</span>
          <i className="gh-abre" aria-hidden="true" />
        </button>
        {verHora && (
          <div className="gh-juego__plata" id="gh-j-plata">
            <form className="gh-juego__hora" onSubmit={e => { e.preventDefault(); confirmarHora(); }}>
              <label htmlFor="gh-j-hora">Una hora de trabajo vale</label>
              <span className="gh-juego__hora-campo">
                <span aria-hidden="true">$</span>
                <input id="gh-j-hora" inputMode="numeric" autoComplete="off" placeholder="0" autoFocus value={horaTexto ? Number(horaTexto).toLocaleString('es-AR') : ''}
                  onChange={e => setHoraTexto(e.target.value.replace(/\D/g, '').slice(0, 7))} onBlur={confirmarHora} />
              </span>
              <button type="submit" className="gh-juego__listo">Calcular</button>
            </form>
            {c.pesosMes
              ? <p>{num(Math.round(c.horasMes))} h por mes × {pesos(n.hora)} = <b>{pesos(c.pesosMes)}</b></p>
              : <p className="gh-juego__ayuda">Poné cuánto te cuesta una hora de trabajo y te decimos cuánto vale el tiempo que recuperás.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CuantoLibera;
